import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, MapRef, Popup, Source } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  IconButton,
  ListItemButton,
  Paper,
  Popover,
  Slider,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as ZoomInIcon,
  Remove as ZoomOutIcon,
  Navigation as CompassIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  MyLocation as MyLocationIcon,
  Layers as LayersIcon,
  Settings as MapSettingsIcon,
  Satellite as SatelliteIcon,
  Title as LabelsIcon,
  Traffic as TrafficIcon,
  Thunderstorm as WeatherIcon,
  KeyboardArrowUp as CollapseToolbarIcon,
  KeyboardArrowDown as ExpandToolbarIcon,
} from '@mui/icons-material';
import { PacketMapPoint, PacketMapPolygon } from '@signals/app-shell/services/intelPacketApi';
import MapStylePopup, { MapType } from '../MapStylePopup';
import {
  DARK_MAP_POPUP_CLASS,
  DarkMapPopupCoords,
  DarkMapPopupRow,
  DarkMapPopupShell,
  useDarkMapPopupChrome,
} from '@signals/app-shell/components/map/DarkMapPopup';
import { useMapAutoResize } from '@signals/app-shell/hooks/useMapAutoResize';
import MapCopOverlays from '@signals/app-shell/features/map/MapCopOverlays';
import { useMapAreaAlerts } from '@signals/app-shell/features/map/useMapAreaAlerts';
import { MapLabelVisibilityContext, MapLayerHiddenContext } from '@signals/app-shell/features/map/mapLayerVisibility';

const KIND_COLOR: Record<string, string> = {
  roster: '#f0b429',
  evidence: '#ff6b5e',
  field_report: '#ff6b5e',
  activity: '#ff6b5e',
  pol: '#41c4b6',
  insights: '#b06ce8',
  insight: '#b06ce8',
  geofence: '#f0b429',
  mission: '#4c8bf5',
  dwells: '#00b8a9',
  threats: '#ef5350',
};

const LAYER_LABELS: Record<string, string> = {
  geofence: 'Fence',
  roster: 'Roster',
  evidence: 'Evidence',
  activity: 'Activity',
  pol: 'PoL',
  insights: 'Insights',
  mission: 'Mission',
  dwells: 'Dwells',
  threats: 'Threats',
};

/** Live mission pins that share these keys even when the packet layer is empty. */
const MISSION_LIVE_LAYERS = ['roster', 'evidence', 'dwells', 'threats', 'mission'] as const;

/**
 * Lowest of the point layers. Children that draw lines (the live mission
 * route) pass this as `beforeId` so waypoint dots stay on top of them.
 */
export const INTEL_POINTS_BASE_LAYER = 'intel-point-glow';

function colorFor(kind?: string): string {
  if (!kind) return '#90caf9';
  if (KIND_COLOR[kind]) return KIND_COLOR[kind];
  const tail = kind.split('-').pop() || '';
  return KIND_COLOR[tail] || '#90caf9';
}

function layerKey(id: string): string {
  const tail = id.split('-').pop() || '';
  if (LAYER_LABELS[tail]) return tail;
  if (tail === 'insight') return 'insights';
  if (tail === 'field_report') return 'evidence';
  return tail;
}

/** ~1 m / 0.001 zoom — tight enough to honour a real focus, loose enough not to chase jitter. */
function viewsMatch(
  latA: number,
  lngA: number,
  zoomA: number,
  latB: number,
  lngB: number,
  zoomB: number,
): boolean {
  return Math.abs(latA - latB) < 1e-5
    && Math.abs(lngA - lngB) < 1e-5
    && Math.abs(zoomA - zoomB) < 1e-3;
}

function readMapView(
  map: { getCenter?: () => { lat: number; lng: number }; getZoom?: () => number } | undefined,
  fallback: { latitude: number; longitude: number; zoom: number },
): { lat: number; lng: number; zoom: number } {
  const center = map?.getCenter?.();
  const zoom = map?.getZoom?.();
  return {
    lat: center && Number.isFinite(center.lat) ? center.lat : fallback.latitude,
    lng: center && Number.isFinite(center.lng) ? center.lng : fallback.longitude,
    zoom: zoom != null && Number.isFinite(zoom) ? zoom : fallback.zoom,
  };
}

export interface IntelMapLayer {
  id: string;
  points: PacketMapPoint[];
  /** Planned route drawn beneath the layer's points. */
  line?: GeoJSON.LineString | null;
}

interface IntelPacketMapProps {
  center: [number, number];
  zoom: number;
  layers: IntelMapLayer[];
  geofences?: PacketMapPolygon[];
  onMove?: (center: [number, number], zoom: number) => void;
  onOpenMapSettings?: () => void;
  /** Extra react-map-gl children (route planner sources, layers, markers). */
  children?: React.ReactNode;
  /** Set while a tool owns the map: suppresses feature popups and reports raw clicks. */
  captureClicks?: boolean;
  onMapClick?: (lngLat: { lng: number; lat: number }) => void;
  cursor?: string;
  /** Hands the underlying mapbox-gl instance to tools that need camera control. */
  onMapReady?: (map: any) => void;
  /**
   * Layer keys that stay listed even with no packet points — the mission
   * overlays (devices, photos, dwells, threats) use the same names.
   */
  liveLayers?: boolean;
}

const KIND_BADGE: Record<string, string> = {
  roster: 'ROSTER',
  evidence: 'EVIDENCE',
  field_report: 'EVIDENCE',
  activity: 'ACTIVITY',
  pol: 'POL',
  insights: 'INSIGHT',
  insight: 'INSIGHT',
  geofence: 'FENCE',
};

type PopupInfo = {
  lng: number;
  lat: number;
  label: string;
  kind: string;
  entityId?: string;
  visits?: number;
  duration?: number;
  timestamp?: string;
};

function ringCoords(ring: number[][]): [number, number][] {
  return (ring || [])
    .filter((c) => Array.isArray(c) && Number.isFinite(Number(c[0])) && Number.isFinite(Number(c[1])))
    .map((c) => [Number(c[0]), Number(c[1])] as [number, number]);
}

const MAP_STYLE_URL: Record<MapType, string> = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
  dark: 'mapbox://styles/mapbox/dark-v11',
  light: 'mapbox://styles/mapbox/light-v11',
  outdoors: 'mapbox://styles/mapbox/outdoors-v12',
};

const IntelPacketMap: React.FC<IntelPacketMapProps> = ({
  center,
  zoom,
  layers,
  geofences,
  onMove,
  onOpenMapSettings,
  children,
  captureClicks,
  onMapClick,
  cursor,
  onMapReady,
  liveLayers,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fences: PacketMapPolygon[] = geofences ?? [];
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fittedKey = useRef<string>('');
  /** Last camera we reported from the map itself, so echoing it cannot tug. */
  const echoedView = useRef<{ lat: number; lng: number; zoom: number } | null>(null);
  const [popup, setPopup] = useState<PopupInfo | null>(null);
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const [toolbarCollapsed, setToolbarCollapsed] = useState(true);
  const [is3D, setIs3D] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [layersAnchor, setLayersAnchor] = useState<HTMLElement | null>(null);
  const [labelsAnchor, setLabelsAnchor] = useState<HTMLElement | null>(null);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showRadar, setShowRadar] = useState(false);
  const [radarOpacity, setRadarOpacity] = useState(0.58);
  const [mapStyle, setMapStyle] = useState<MapType>('dark');
  const [styleAnchor, setStyleAnchor] = useState<HTMLElement | null>(null);
  const [showIconLabels, setShowIconLabels] = useState(
    () => window.localStorage.getItem('intel-icon-labels') !== 'false',
  );
  const [showMapLabels, setShowMapLabels] = useState(
    () => window.localStorage.getItem('intel-map-labels') !== 'false',
  );
  const token = process.env.REACT_APP_MAPBOX_TOKEN || '';
  useDarkMapPopupChrome(!!popup);

  const map = () => mapRef.current?.getMap();

  const getTrafficViewport = useCallback(() => {
    const m = mapRef.current?.getMap();
    if (!m) return null;
    const bounds = m.getBounds();
    return {
      bounds: {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      },
      zoom: m.getZoom(),
    };
  }, []);

  const { incidents, weatherAlerts, reload: reloadTrafficAlerts } = useMapAreaAlerts(
    showTraffic,
    getTrafficViewport,
  );

  // The reference dock and the left panel both change this container's width
  // without the window ever resizing, which Mapbox does not notice by itself.
  useMapAutoResize(containerRef, useCallback(() => mapRef.current?.getMap(), []));

  const geojson = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: layers.flatMap((layer) => {
      const bucket = layerKey(layer.id);
      if (hidden[bucket]) return [];
      return (layer.points || [])
        .filter((p) => Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude)))
        .map((p, idx) => {
          const kind = p.kind || bucket;
          return {
            type: 'Feature' as const,
            id: idx + bucket.length * 10000,
            properties: {
              label: p.label,
              kind,
              color: colorFor(kind),
              entityId: p.entityId || '',
              visits: p.visits ?? 0,
              duration: p.duration ?? 0,
              timestamp: p.timestamp || '',
            },
            geometry: {
              type: 'Point' as const,
              coordinates: [Number(p.longitude), Number(p.latitude)],
            },
          };
        });
    }),
  }), [layers, hidden]);

  const lineGeojson = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: layers
      .filter((layer) => !hidden[layerKey(layer.id)] && (layer.line?.coordinates?.length || 0) > 1)
      .map((layer) => ({
        type: 'Feature' as const,
        properties: { color: colorFor(layerKey(layer.id)) },
        geometry: layer.line as GeoJSON.LineString,
      })),
  }), [layers, hidden]);

  const fenceGeojson = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: hidden.geofence
      ? []
      : fences
        .map((fence, idx) => {
          const ring = ringCoords(fence.coordinates);
          if (ring.length < 3) return null;
          if (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) {
            ring.push(ring[0]);
          }
          return {
            type: 'Feature' as const,
            id: idx,
            properties: {
              label: fence.label,
              kind: 'geofence',
              color: colorFor('geofence'),
              entityId: fence.entityId || '',
            },
            geometry: { type: 'Polygon' as const, coordinates: [ring] },
          };
        })
        .filter((f): f is NonNullable<typeof f> => !!f),
  }), [fences, hidden]);

  /** Drawn features per layer, so the selector can dim the ones with nothing in them. */
  const layerCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.keys(LAYER_LABELS).forEach((k) => { counts[k] = 0; });
    layers.forEach((layer) => {
      const bucket = layerKey(layer.id);
      if (!(bucket in counts)) return;
      counts[bucket] += (layer.points || []).filter(
        (p) => Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude)),
      ).length;
      // A planned route is a feature in its own right: a mission can have one
      // with no waypoints of its own, and calling that layer empty would be a lie.
      if ((layer.line?.coordinates?.length || 0) > 1) counts[bucket] += 1;
    });
    counts.geofence += fences.length;
    return counts;
  }, [layers, fences]);

  const liveKeys = useMemo(
    () => new Set(liveLayers ? MISSION_LIVE_LAYERS : []),
    [liveLayers],
  );
  const populated = useMemo(
    () => Object.keys(LAYER_LABELS).filter((k) => layerCounts[k] > 0 || liveKeys.has(k)),
    [layerCounts, liveKeys],
  );

  const applyBasemapLabels = useCallback((visible: boolean) => {
    const gl = map();
    const layers = gl?.getStyle?.()?.layers;
    if (!gl || !layers) return;
    layers.forEach((layer: { id: string; type: string }) => {
      if (layer.type !== 'symbol') return;
      if (layer.id.startsWith('intel-')) return;
      try {
        gl.setLayoutProperty(layer.id, 'visibility', visible ? 'visible' : 'none');
      } catch {
        // Style swaps drop layers mid-pass.
      }
    });
  }, []);

  useEffect(() => {
    window.localStorage.setItem('intel-icon-labels', String(showIconLabels));
    window.localStorage.setItem('intel-map-labels', String(showMapLabels));
    applyBasemapLabels(showMapLabels);
    const gl = map();
    if (!gl) return undefined;
    const apply = () => applyBasemapLabels(showMapLabels);
    gl.once('idle', apply);
    return undefined;
  }, [showIconLabels, showMapLabels, mapStyle, applyBasemapLabels]);
  // Drives the toolbar badge. Without it, moving the legend into a popover
  // would leave a hidden layer with no cue that anything is switched off.
  const hiddenCount = populated.filter((k) => hidden[k]).length;

  const fitKey = [
    ...layers.map((l) => `${layerKey(l.id)}:${(l.points || []).length}:${l.line?.coordinates?.length || 0}`),
    `geofence:${fences.length}`,
  ].join('|');

  const fitToPoints = () => {
    const map = mapRef.current;
    const coords = [
      ...geojson.features.map((f) => f.geometry.coordinates as [number, number]),
      ...lineGeojson.features.flatMap((f) => (f.geometry.coordinates || []) as [number, number][]),
      ...fenceGeojson.features.flatMap((f) => (f.geometry.coordinates[0] || []) as [number, number][]),
    ];
    if (!map || coords.length === 0) return;
    if (coords.length === 1) {
      map.flyTo({ center: coords[0], zoom: Math.max(zoom, 14), duration: 500 });
      return;
    }
    const lons = coords.map((c) => c[0]);
    const lats = coords.map((c) => c[1]);
    map.fitBounds(
      [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
      { padding: 72, maxZoom: 15, duration: 600 },
    );
  };

  useEffect(() => {
    if (!geojson.features.length || fittedKey.current === fitKey) return;
    fittedKey.current = fitKey;
    const t = window.setTimeout(fitToPoints, 200);
    return () => window.clearTimeout(t);
  }, [fitKey, geojson.features.length]);

  /**
   * The camera belongs to Mapbox. `center` and `zoom` only *request* changes.
   *
   * Passing longitude/latitude/zoom straight to `<Map>` makes it a controlled
   * map, and react-map-gl then reconciles on every render by mutating its own
   * render transform — the one the basemap is drawn with. Markers do not use
   * that transform; they position themselves against `map.transform`, which
   * follows the user's gesture. The two agree only while the props are current,
   * and this map reports movement on `moveend` alone. So any render during a
   * drag draws the basemap at the stale camera while the pins sit at the real
   * one, and the pins appear to slide off the map. It shows up on missions
   * because that is the page that re-renders constantly, once per device
   * report.
   *
   * Reporting on every frame instead would fix the divergence and re-render the
   * whole page at 60fps to do it. Letting Mapbox keep its own camera costs
   * nothing and removes the second source of truth.
   *
   * `moveend`'s `viewState` is that same stale transform. Reading it back as
   * `center` then easeTo-ing it fights a live `flyTo` (Activity → Details on
   * a signal, or picking a device) and the map oscillates between the pin
   * that just moved and the last follow the wrapper still believes in.
   */
  useEffect(() => {
    const m = mapRef.current;
    // Before load, the initial view state already carries these.
    if (!m) return;
    const moving = (m.getMap?.() ?? m) as { isMoving?: () => boolean };
    if (moving.isMoving?.()) return;
    const shown = m.getCenter();
    const echoed = echoedView.current;
    // The parent echoes our own `moveend` back as state. Re-issuing the camera
    // we just reported would tug the map every time a device moved.
    if (
      echoed
      && viewsMatch(center[0], center[1], zoom, echoed.lat, echoed.lng, echoed.zoom)
    ) {
      return;
    }
    if (viewsMatch(shown.lat, shown.lng, m.getZoom(), center[0], center[1], zoom)) {
      return;
    }
    m.easeTo({ center: [center[1], center[0]], zoom, duration: 500 });
  }, [center, zoom]);

  if (!token) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2" color="text.secondary">Mapbox token is not configured.</Typography>
      </Box>
    );
  }

  const toolbarBtnSx = {
    borderRadius: 0,
    p: 1,
    color: isDark ? 'white' : 'inherit',
  } as const;

  return (
    <MapLayerHiddenContext.Provider value={hidden}>
    <MapLabelVisibilityContext.Provider value={{ icons: showIconLabels, map: showMapLabels }}>
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
        '& .mapboxgl-marker': { zIndex: 2 },
      }}
    >
      <Map
        ref={mapRef}
        mapboxAccessToken={token}
        initialViewState={{ longitude: center[1], latitude: center[0], zoom }}
        mapStyle={MAP_STYLE_URL[mapStyle]}
        reuseMaps
        onLoad={() => {
          fitToPoints();
          applyBasemapLabels(showMapLabels);
          onMapReady?.(mapRef.current?.getMap());
        }}
        onMoveEnd={(e) => {
          const view = readMapView(e.target, e.viewState);
          echoedView.current = view;
          onMove?.([view.lat, view.lng], view.zoom);
          if (showTraffic) void reloadTrafficAlerts();
        }}
        cursor={cursor}
        onMouseMove={(e) => {
          if (cursor) return;
          const hit = e.features?.some((f) => f.layer?.id === 'intel-points' || f.layer?.id === 'intel-point-ring' || f.layer?.id === 'intel-fence-fill');
          e.target.getCanvas().style.cursor = hit ? 'pointer' : '';
        }}
        onMouseLeave={(e) => {
          if (cursor) return;
          e.target.getCanvas().style.cursor = '';
        }}
        onClick={(e) => {
          if (captureClicks) {
            onMapClick?.({ lng: e.lngLat.lng, lat: e.lngLat.lat });
            return;
          }
          const feat = e.features?.find((f) => f.layer?.id === 'intel-points' || f.layer?.id === 'intel-point-ring' || f.layer?.id === 'intel-fence-fill');
          if (!feat) {
            setPopup(null);
            return;
          }
          const props = feat.properties || {};
          let lng = e.lngLat.lng;
          let lat = e.lngLat.lat;
          if (feat.geometry.type === 'Point') {
            const coords = feat.geometry.coordinates as [number, number];
            lng = coords[0];
            lat = coords[1];
          }
          setPopup({
            lng,
            lat,
            label: String(props.label || ''),
            kind: String(props.kind || ''),
            entityId: props.entityId ? String(props.entityId) : undefined,
            visits: Number(props.visits) || undefined,
            duration: Number(props.duration) || undefined,
            timestamp: props.timestamp ? String(props.timestamp) : undefined,
          });
        }}
        interactiveLayerIds={['intel-points', 'intel-point-ring', 'intel-fence-fill']}
      >
        <MapCopOverlays
          traffic={showTraffic}
          radar={showRadar}
          radarOpacity={radarOpacity}
          incidents={incidents}
          weatherAlerts={weatherAlerts}
        />
        <Source id="intel-fences" type="geojson" data={fenceGeojson}>
          <Layer
            id="intel-fence-fill"
            type="fill"
            paint={{
              'fill-color': '#00bcd4',
              'fill-opacity': 0.18,
            }}
          />
          <Layer
            id="intel-fence-line"
            type="line"
            paint={{
              'line-color': '#00bcd4',
              'line-width': 2,
              'line-opacity': 0.95,
            }}
          />
          {/* Named on the map, not only in the click popup: a watched area an
              operator has to click to identify is one they will not identify. */}
          <Layer
            id="intel-fence-label"
            type="symbol"
            layout={{
              visibility: showIconLabels ? 'visible' : 'none',
              'text-field': ['get', 'label'],
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 11,
              'text-anchor': 'center',
            }}
            paint={{
              'text-color': '#c9f4fa',
              'text-halo-color': '#0b1017',
              'text-halo-width': 1.6,
            }}
          />
        </Source>
        <Source id="intel-routes" type="geojson" data={lineGeojson}>
          <Layer
            id="intel-route-casing"
            type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{ 'line-color': '#0b1017', 'line-width': 7, 'line-opacity': 0.75 }}
          />
          <Layer
            id="intel-route-line"
            type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{ 'line-color': ['get', 'color'], 'line-width': 4, 'line-opacity': 0.95 }}
          />
        </Source>
        <Source id="intel-points" type="geojson" data={geojson}>
          {/* Mission stops are HTML markers (MissionWaypointLayer) so the
              live route cannot paint over them. Keep them in the source for
              fit-bounds and the layer count; skip the GL circle/label. */}
          <Layer
            id="intel-point-glow"
            type="circle"
            filter={['!=', ['get', 'kind'], 'mission']}
            paint={{
              'circle-radius': 10,
              'circle-blur': 1.4,
              'circle-color': ['get', 'color'],
              'circle-opacity': 0.4,
            }}
          />
          <Layer
            id="intel-point-ring"
            type="circle"
            filter={['!=', ['get', 'kind'], 'mission']}
            paint={{
              'circle-radius': 5.5,
              'circle-color': ['get', 'color'],
              'circle-opacity': 0,
              'circle-stroke-width': 1,
              'circle-stroke-color': ['get', 'color'],
            }}
          />
          <Layer
            id="intel-points"
            type="circle"
            filter={['!=', ['get', 'kind'], 'mission']}
            paint={{
              'circle-radius': 3.5,
              'circle-color': ['get', 'color'],
              'circle-stroke-width': 1.5,
              'circle-stroke-color': '#ffffff',
              'circle-opacity': 1,
            }}
          />
          <Layer
            id="intel-point-label"
            type="symbol"
            filter={['!=', ['get', 'kind'], 'mission']}
            layout={{
              visibility: showIconLabels ? 'visible' : 'none',
              'text-field': ['get', 'label'],
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 10,
              'text-anchor': 'top',
              'text-offset': [0, 0.8],
            }}
            paint={{
              'text-color': '#e8eef7',
              'text-halo-color': '#0b1017',
              'text-halo-width': 1.5,
            }}
          />
        </Source>
        {popup && (
          <Popup
            longitude={popup.lng}
            latitude={popup.lat}
            onClose={() => setPopup(null)}
            closeButton={false}
            closeOnClick={false}
            anchor="bottom"
            offset={12}
            className={DARK_MAP_POPUP_CLASS}
            maxWidth="320px"
            style={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
          >
            <DarkMapPopupShell
              accent={colorFor(popup.kind)}
              title={popup.label || popup.kind || 'Feature'}
              badge={KIND_BADGE[popup.kind] || popup.kind.toUpperCase()}
              onClose={() => setPopup(null)}
            >
              {popup.entityId && popup.entityId !== popup.label && (
                <DarkMapPopupRow label="Entity">{popup.entityId}</DarkMapPopupRow>
              )}
              <DarkMapPopupRow label="Layer">{LAYER_LABELS[popup.kind] || popup.kind}</DarkMapPopupRow>
              {popup.visits != null && popup.visits > 0 && (
                <DarkMapPopupRow label="Visits">{popup.visits}</DarkMapPopupRow>
              )}
              {popup.duration != null && popup.duration > 0 && (
                <DarkMapPopupRow label="Dwell">
                  {popup.duration >= 60 ? `${Math.round(popup.duration / 60)} min` : `${popup.duration}s`}
                </DarkMapPopupRow>
              )}
              {popup.timestamp && (
                <DarkMapPopupRow label="Time">
                  {new Date(popup.timestamp).toLocaleString()}
                </DarkMapPopupRow>
              )}
              <DarkMapPopupCoords lat={popup.lat} lon={popup.lng} />
            </DarkMapPopupShell>
          </Popup>
        )}
        {children}
      </Map>
      <Paper
        elevation={2}
        sx={{
          position: 'absolute',
          top: 10,
          right: 28,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none',
        }}
      >
        <Tooltip
          title={
            toolbarCollapsed
              ? (hiddenCount ? `Expand toolbar — ${hiddenCount} layer(s) hidden` : 'Expand toolbar')
              : 'Collapse toolbar'
          }
          placement="left"
        >
          <IconButton
            size="small"
            onClick={() => setToolbarCollapsed((v) => !v)}
            sx={toolbarBtnSx}
            aria-label={toolbarCollapsed ? 'Expand map toolbar' : 'Collapse map toolbar'}
          >
            {/* The toolbar starts collapsed, so a hidden layer would otherwise
                have no cue anywhere on the map once the legend moved inside. */}
            <Badge
              badgeContent={toolbarCollapsed ? hiddenCount : 0}
              color="warning"
              overlap="circular"
              sx={{ '& .MuiBadge-badge': { height: 14, minWidth: 14, fontSize: '0.6rem' } }}
            >
              {toolbarCollapsed ? <ExpandToolbarIcon fontSize="small" /> : <CollapseToolbarIcon fontSize="small" />}
            </Badge>
          </IconButton>
        </Tooltip>
        {!toolbarCollapsed && (
          <>
            <Tooltip title="Zoom In" placement="left">
              <IconButton size="small" sx={toolbarBtnSx} onClick={() => map()?.zoomIn()}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom Out" placement="left">
              <IconButton size="small" sx={toolbarBtnSx} onClick={() => map()?.zoomOut()}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset North" placement="left">
              <IconButton
                size="small"
                sx={toolbarBtnSx}
                onClick={() => map()?.easeTo({ bearing: 0, pitch: is3D ? 60 : 0, duration: 400 })}
              >
                <CompassIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} placement="left">
              <IconButton
                size="small"
                sx={toolbarBtnSx}
                onClick={() => {
                  const el = containerRef.current;
                  if (!el) return;
                  if (document.fullscreenElement) {
                    document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
                  } else {
                    el.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
                  }
                }}
              >
                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'inherit' }} />
            <Tooltip title="My Location" placement="left">
              <IconButton
                size="small"
                sx={toolbarBtnSx}
                onClick={() => {
                  navigator.geolocation?.getCurrentPosition((pos) => {
                    map()?.flyTo({
                      center: [pos.coords.longitude, pos.coords.latitude],
                      zoom: 14,
                      duration: 800,
                    });
                  });
                }}
              >
                <MyLocationIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={is3D ? 'Switch to 2D' : 'Switch to 3D'} placement="left">
              <IconButton
                size="small"
                onClick={() => {
                  const next = !is3D;
                  setIs3D(next);
                  map()?.easeTo({ pitch: next ? 60 : 0, duration: 400 });
                }}
                sx={{
                  ...toolbarBtnSx,
                  bgcolor: is3D ? 'primary.main' : 'transparent',
                  color: is3D ? 'white' : toolbarBtnSx.color,
                  '&:hover': { bgcolor: is3D ? 'primary.dark' : 'action.hover' },
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '0.75rem' }}>3D</Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title={showTraffic ? 'Hide traffic and incidents' : 'Show traffic and incidents'} placement="left">
              <IconButton
                size="small"
                onClick={() => setShowTraffic((on) => !on)}
                aria-label={showTraffic ? 'Hide traffic and incidents' : 'Show traffic and incidents'}
                sx={{
                  ...toolbarBtnSx,
                  bgcolor: showTraffic ? '#4caf50' : 'transparent',
                  color: showTraffic ? 'white' : toolbarBtnSx.color,
                  '&:hover': { bgcolor: showTraffic ? '#388e3c' : 'action.hover' },
                }}
              >
                <TrafficIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Tooltip title={showRadar ? 'Hide weather radar' : 'Show weather radar'} placement="left">
                <IconButton
                  size="small"
                  onClick={() => setShowRadar((on) => !on)}
                  aria-label={showRadar ? 'Hide weather radar' : 'Show weather radar'}
                  sx={{
                    ...toolbarBtnSx,
                    bgcolor: showRadar ? '#03a9f4' : 'transparent',
                    color: showRadar ? 'white' : toolbarBtnSx.color,
                    '&:hover': { bgcolor: showRadar ? '#0288d1' : 'action.hover' },
                  }}
                >
                  <WeatherIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Collapse in={showRadar} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    width: 32,
                    height: 104,
                    my: 0.25,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isDark ? 'rgba(3,169,244,0.14)' : 'rgba(3,169,244,0.10)',
                    border: '1px solid rgba(3,169,244,0.35)',
                    borderRadius: 1,
                  }}
                  onClick={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: '#03a9f4', fontWeight: 700, fontSize: '0.55rem', lineHeight: 1, mb: 0.5 }}
                  >
                    {Math.round(radarOpacity * 100)}%
                  </Typography>
                  <Slider
                    aria-label="Weather radar opacity"
                    orientation="vertical"
                    min={0.1}
                    max={0.9}
                    step={0.05}
                    value={radarOpacity}
                    onChange={(_event, value) => setRadarOpacity(Array.isArray(value) ? value[0] : value)}
                    size="small"
                    sx={{
                      color: '#03a9f4',
                      height: 68,
                      '& .MuiSlider-rail': { opacity: 0.35 },
                      '& .MuiSlider-thumb': { width: 12, height: 12 },
                    }}
                  />
                </Box>
              </Collapse>
            </Box>
            <Tooltip
              title={
                !showIconLabels && !showMapLabels
                  ? 'Labels (all hidden)'
                  : !showIconLabels
                    ? 'Labels (icons hidden)'
                    : !showMapLabels
                      ? 'Labels (map hidden)'
                      : 'Labels'
              }
              placement="left"
            >
              <IconButton
                size="small"
                onClick={(e) => setLabelsAnchor(e.currentTarget)}
                aria-label="Label visibility"
                sx={{
                  ...toolbarBtnSx,
                  bgcolor: !showIconLabels || !showMapLabels || labelsAnchor ? 'primary.main' : 'transparent',
                  color: !showIconLabels || !showMapLabels || labelsAnchor ? 'white' : toolbarBtnSx.color,
                  '&:hover': {
                    bgcolor: !showIconLabels || !showMapLabels || labelsAnchor ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                <LabelsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={hiddenCount ? `Layers (${hiddenCount} hidden)` : 'Layers'}
              placement="left"
            >
              <IconButton
                size="small"
                onClick={(e) => setLayersAnchor(e.currentTarget)}
                aria-label="Map layers"
                sx={{
                  ...toolbarBtnSx,
                  bgcolor: layersAnchor ? 'primary.main' : 'transparent',
                  color: layersAnchor ? 'white' : toolbarBtnSx.color,
                  '&:hover': {
                    bgcolor: layersAnchor ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                <Badge
                  badgeContent={hiddenCount}
                  color="warning"
                  overlap="circular"
                  sx={{ '& .MuiBadge-badge': { height: 14, minWidth: 14, fontSize: '0.6rem' } }}
                >
                  <LayersIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="Map Settings (playback)" placement="left">
              <IconButton size="small" sx={toolbarBtnSx} onClick={() => onOpenMapSettings?.()}>
                <MapSettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'inherit' }} />
        <Tooltip title="Map Style" placement="left">
          <IconButton
            size="small"
            sx={toolbarBtnSx}
            onClick={(e) => setStyleAnchor(e.currentTarget)}
          >
            <SatelliteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
      <MapStylePopup
        open={!!styleAnchor}
        onClose={() => setStyleAnchor(null)}
        selectedMapType={mapStyle}
        onMapTypeChange={(next) => {
          setMapStyle(next);
          setStyleAnchor(null);
        }}
      />
      <Popover
        open={!!labelsAnchor}
        anchorEl={labelsAnchor}
        onClose={() => setLabelsAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mr: 1,
            minWidth: 200,
            py: 0.5,
            borderRadius: 1,
            bgcolor: isDark ? 'rgba(30,30,30,0.97)' : 'background.paper',
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : undefined,
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{ display: 'block', px: 1.25, pt: 0.5, pb: 0.25, fontWeight: 700, letterSpacing: 0.6, opacity: 0.7 }}
        >
          LABELS
        </Typography>
        {([
          {
            key: 'icons',
            label: 'Icon names',
            hint: 'Pins on the map',
            checked: showIconLabels,
            onToggle: () => setShowIconLabels((on) => !on),
          },
          {
            key: 'map',
            label: 'Map places',
            hint: 'Streets and towns',
            checked: showMapLabels,
            onToggle: () => setShowMapLabels((on) => !on),
          },
        ] as const).map((row) => (
          <ListItemButton
            key={row.key}
            dense
            onClick={row.onToggle}
            sx={{ py: 0.4, pl: 1, pr: 1.25 }}
          >
            <Checkbox
              size="small"
              tabIndex={-1}
              disableRipple
              checked={row.checked}
              sx={{ p: 0.5, mr: 0.5 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                {row.label}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.55, display: 'block' }}>
                {row.hint}
              </Typography>
            </Box>
          </ListItemButton>
        ))}
      </Popover>
      <Popover
        open={!!layersAnchor}
        anchorEl={layersAnchor}
        onClose={() => setLayersAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mr: 1,
            minWidth: 216,
            py: 0.5,
            borderRadius: 1,
            bgcolor: isDark ? 'rgba(30,30,30,0.97)' : 'background.paper',
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : undefined,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1.25, pr: 0.5, pb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ flex: 1, fontWeight: 700, letterSpacing: 0.6, opacity: 0.7 }}
          >
            LAYERS
          </Typography>
          <Button
            size="small"
            disabled={hiddenCount === 0}
            onClick={() => setHidden({})}
            sx={{ minWidth: 0, px: 0.75, fontSize: '0.7rem' }}
          >
            All
          </Button>
          <Button
            size="small"
            disabled={!populated.length || hiddenCount === populated.length}
            onClick={() => setHidden(Object.fromEntries(populated.map((k) => [k, true])))}
            sx={{ minWidth: 0, px: 0.75, fontSize: '0.7rem' }}
          >
            None
          </Button>
        </Box>
        <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.12)' : undefined }} />
        {populated.map((key) => {
          const label = LAYER_LABELS[key];
          const count = layerCounts[key] || 0;
          return (
            <ListItemButton
              key={key}
              dense
              onClick={() => setHidden((prev) => ({ ...prev, [key]: !prev[key] }))}
              sx={{ py: 0.25, pl: 1, pr: 1.25 }}
            >
              {/* No `edge="start"`: it exists to cancel a list item's default
                  left padding and applies a negative margin to do it, which
                  against this row's tighter padding pulled the box onto the
                  popover's border and clipped it. */}
              <Checkbox
                size="small"
                tabIndex={-1}
                disableRipple
                checked={!hidden[key]}
                sx={{
                  p: 0.5,
                  mr: 0.5,
                  color: colorFor(key),
                  '&.Mui-checked': { color: colorFor(key) },
                }}
              />
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                {label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.55, fontVariantNumeric: 'tabular-nums' }}
              >
                {count || '—'}
              </Typography>
            </ListItemButton>
          );
        })}
        <Divider sx={{ my: 0.5, borderColor: isDark ? 'rgba(255,255,255,0.12)' : undefined }} />
        <Typography
          variant="caption"
          sx={{ display: 'block', px: 1.25, pt: 0.25, pb: 0.25, fontWeight: 700, letterSpacing: 0.6, opacity: 0.7 }}
        >
          LIVE OVERLAYS
        </Typography>
        {([
          { key: 'traffic', label: 'Traffic & incidents', checked: showTraffic, onToggle: () => setShowTraffic((on) => !on) },
          { key: 'radar', label: 'Weather radar', checked: showRadar, onToggle: () => setShowRadar((on) => !on) },
        ] as const).map((row) => (
          <ListItemButton
            key={row.key}
            dense
            onClick={row.onToggle}
            sx={{ py: 0.25, pl: 1, pr: 1.25 }}
          >
            <Checkbox
              size="small"
              tabIndex={-1}
              disableRipple
              checked={row.checked}
              sx={{ p: 0.5, mr: 0.5 }}
            />
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
              {row.label}
            </Typography>
          </ListItemButton>
        ))}
      </Popover>
    </Box>
    </MapLabelVisibilityContext.Provider>
    </MapLayerHiddenContext.Provider>
  );
};

export default IntelPacketMap;
